import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  SquaresFour,
  Users,
  Briefcase,
  CheckSquare,
  Folders,
  ChatCircleText,
  Receipt,
  ChartBar,
  UsersThree,
  SignOut,
  Scales,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/matters", label: "Matters", icon: Briefcase },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/documents", label: "Documents", icon: Folders },
  { to: "/communications", label: "Communications", icon: ChatCircleText },
  { to: "/billing", label: "Billing", icon: Receipt },
  { to: "/reports", label: "Reports", icon: ChartBar },
];

const roleLabel = {
  partner: "Managing Partner",
  advicer: "Immigration Adviser",
  paralegal: "Paralegal",
  caseworker: "Caseworker",
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const crumb = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label
    || (location.pathname.startsWith("/matters/") ? "Matter Detail"
      : location.pathname.startsWith("/users") ? "Users"
      : "Overview");

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex w-64 shrink-0 flex-col bg-[#0B1528] text-white sidebar-scroll"
      >
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Scales size={22} weight="light" className="text-[#C5A059]" />
            <div>
              <div className="font-serif-brand text-lg leading-tight">Arden Leigh</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400">LLP · Immigration</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-[#C5A059] pl-[10px]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="light" />
              <span>{label}</span>
            </NavLink>
          ))}

          {user?.role === "partner" && (
            <NavLink
              to="/users"
              data-testid="nav-users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-[#C5A059] pl-[10px]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <UsersThree size={18} weight="light" />
              <span>Team</span>
            </NavLink>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-slate-400 mb-3">{roleLabel[user?.role] || user?.role}</div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200">
          <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Arden Leigh LLP</div>
              <h1 className="text-lg font-medium tracking-tight" data-testid="page-title">{crumb}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:block text-right">
                <div className="text-xs text-slate-500">{roleLabel[user?.role] || user?.role}</div>
                <div className="text-sm font-medium">{user?.name}</div>
              </div>
              <div className="h-9 w-9 rounded-sm bg-[#0B1528] text-white flex items-center justify-center text-sm font-medium">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <Button
                variant="outline"
                size="sm"
                data-testid="header-logout"
                className="lg:hidden rounded-sm"
                onClick={async () => { await logout(); navigate("/login"); }}
              >
                <SignOut size={14} />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-6 lg:py-8 animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
